
import type { PropType } from 'vue'
import type { FiltersMessages, RealtimeFilter } from '../../models'
import { computed, defineComponent, getCurrentInstance } from 'vue'
import { FilterIdEnum } from '../../enums'
import { compileMessage } from '../../utils/messages.utils'
import { UiMenu, UiPopover } from '../keen-ui'
import { EpbBaseInput } from '../shared/epb-base-input'
import { EpbButton } from '../shared/epb-button'

export interface UiMenuOption {
  id: number
  label: string
}

export default defineComponent({
  name: 'RealtimeFilterSelector',
  components: {
    EpbBaseInput,
    EpbButton,
    UiMenu,
    UiPopover,
  },
  props: {
    filter: {
      type: Object as PropType<RealtimeFilter>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const baseClass = 'epb-realtime-filter-selector'

    const filtersMessages = ctx.proxy.$epb.messages.filters as FiltersMessages
    const messages = {
      name: filtersMessages.names[FilterIdEnum.REALTIME],
      values: filtersMessages.values[FilterIdEnum.REALTIME],
    }

    const options = computed<Array<UiMenuOption>>(() =>
      props.filter.config.intervalsInMinutes.reduce<Array<UiMenuOption>>(
        (result, intervalInMinutes) => {
          if (
            !props.filter.config.maxPeriodInMinutes ||
            intervalInMinutes <= props.filter.config.maxPeriodInMinutes
          ) {
            result.push({
              id: intervalInMinutes,
              label: getIntervalInMinutesText(intervalInMinutes),
            })
          }
          return result
        },
        [],
      ),
    )

    const selectedValuesText = computed<string>(() =>
      getIntervalInMinutesText(props.filter.selectedValues, true),
    )

    function selectOption(option: UiMenuOption): void {
      emit('selectedValuesChange', option.id)
    }

    function getIntervalInMinutesText(intervalInMinutes: number, abbr = false): string {
      let result: string
      if (intervalInMinutes > 60) {
        const message = abbr ? messages.values.hours.short : messages.values.hours.long
        const hours = Math.floor(intervalInMinutes / 60)
        result = compileMessage(message, { hours: hours.toString() })
      } else {
        const message = abbr ? messages.values.minutes.short : messages.values.minutes.long
        result = compileMessage(message, { minutes: intervalInMinutes.toString() })
      }
      return result
    }

    return {
      baseClass,
      messages,
      options,
      selectedValuesText,
      selectOption,
    }
  },
})
